import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { Mag } from '../components/mag.js';
import { magToast } from '../components/toaster.js';
import { listMetaAdAccounts, type MetaAdAccountDTO, setMetaAdAccount } from '../lib/api.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import { brandsQuery } from '../lib/queries.js';

// /brand/$slug/meta-connect/ad-account — picker (s192).
//
// Lists the ad accounts the connected meta user can access + lets
// the founder pick one. Persisted on brand_meta_connections; once
// set, paid-ads code (future) reads from there.
//
// Reachable from settings/integrations → meta card → "pick ad
// account" CTA. Brand context comes from the URL slug, same
// pattern as the page picker. Back link always works.

export const Route = createFileRoute('/brand/$slug/meta-connect/ad-account')({
  component: AdAccountPickerPage,
});

function AdAccountPickerPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { slug } = Route.useParams();
  const [submittingId, setSubmittingId] = useState<string | null>(null);

  const brandsQ = useQuery(brandsQuery());
  const brand = brandsQ.data?.find((b) => b.slug === slug) ?? null;

  const accountsQ = useQuery({
    queryKey: ['meta-ad-accounts', brand?.id ?? 'none'],
    queryFn: () => (brand ? listMetaAdAccounts(brand.id) : Promise.resolve([])),
    enabled: Boolean(brand),
    retry: false,
  });

  const onPick = async (account: MetaAdAccountDTO) => {
    if (!brand) return;
    if (account.accountStatus !== 1) {
      magToast.error(
        i18n._(msg`this account is disabled.`),
        i18n._(msg`meta lists it but won't accept publishes — pick an active one.`),
      );
      return;
    }
    setSubmittingId(account.id);
    try {
      await setMetaAdAccount(brand.id, account.id);
      magToast.success(
        i18n._(msg`locked in.`),
        i18n._(msg`mag will publish paid ads under ${account.name}.`),
      );
      await queryClient.invalidateQueries({ queryKey: ['brand-meta-connection', brand.id] });
      await router.invalidate();
      await navigate({
        to: '/brand/$slug/settings/integrations',
        params: { slug },
      });
    } catch (err) {
      magToast.error("couldn't pick it.", founderErrorCopy(err));
      setSubmittingId(null);
    }
  };

  return (
    <main className="min-h-screen bg-[var(--cream)]">
      <section className="mx-auto max-w-[680px] px-4 pb-16 pt-12 sm:px-7">
        <Link
          to="/brand/$slug/settings/integrations"
          params={{ slug }}
          className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--ink)]/55 hover:text-[var(--ink)]"
        >
          {'←'} <Trans>back to settings</Trans>
        </Link>
        <div className="mt-6 flex items-start gap-3">
          <Mag size={44} mood="smug" />
          <div className="flex-1">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
              {'↘'} <Trans>connect meta</Trans>
            </p>
            <h1 className="mt-1 font-display text-[28px] font-black leading-[1.05] tracking-[-0.04em] sm:text-[34px]">
              <Trans>pick the ad account.</Trans>
            </h1>
            <p className="mt-4 font-serif text-[16px] leading-[1.5] text-[var(--ink)]/85">
              <Trans>
                “which of your meta ad accounts should i publish paid ads under for this brand?”
              </Trans>
            </p>

            <div className="mt-6">
              {accountsQ.isLoading ? (
                <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
                  {'↘'} <Trans>asking meta for your ad accounts…</Trans>
                </p>
              ) : accountsQ.isError ? (
                <article className="rounded-xl border-[1.5px] border-[var(--plum)]/40 bg-[var(--spell-soft)]/40 p-5">
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
                    {'↘'} <Trans>couldn't fetch your ad accounts</Trans>
                  </p>
                  <p className="mt-2 text-[13px] leading-[1.5] text-[var(--ink)]/80">
                    {accountsQ.error instanceof Error ? (
                      accountsQ.error.message
                    ) : (
                      <Trans>unknown error.</Trans>
                    )}
                  </p>
                  <p className="mt-3 text-[12.5px] leading-[1.5] text-[var(--ink)]/65">
                    <Trans>
                      if this connection was made before s192, reconnect from settings to enable
                      ad-account selection.
                    </Trans>
                  </p>
                </article>
              ) : (accountsQ.data ?? []).length === 0 ? (
                <article className="rounded-xl border border-dashed border-[var(--ink)]/20 bg-[var(--paper)]/40 p-5">
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]/55">
                    {'↘'} <Trans>no ad accounts</Trans>
                  </p>
                  <p className="mt-2 text-[13px] leading-[1.5] text-[var(--ink)]/65">
                    <Trans>
                      your meta user doesn't have access to any ad accounts. create one in meta
                      business suite + come back.
                    </Trans>
                  </p>
                </article>
              ) : (
                <ul className="flex flex-col gap-2">
                  {(accountsQ.data ?? []).map((a) => (
                    <AdAccountRow
                      key={a.id}
                      account={a}
                      onPick={() => void onPick(a)}
                      submitting={submittingId === a.id}
                      disabled={submittingId !== null && submittingId !== a.id}
                    />
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function AdAccountRow({
  account,
  onPick,
  submitting,
  disabled,
}: {
  account: MetaAdAccountDTO;
  onPick: () => void;
  submitting: boolean;
  disabled: boolean;
}) {
  const isActive = account.accountStatus === 1;
  return (
    <li
      className={`flex items-center justify-between gap-4 rounded-xl border border-[var(--ink)]/15 bg-white p-4 ${
        isActive ? '' : 'opacity-60'
      }`}
    >
      <div>
        <p className="font-serif text-[16px] leading-tight text-[var(--ink)]">{account.name}</p>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
          ↘ {account.id}
          {account.currency ? ` · ${account.currency}` : ''}
          {!isActive ? ` · ${i18n._(msg`disabled by meta`)}` : ''}
        </p>
      </div>
      <button
        type="button"
        onClick={onPick}
        disabled={disabled || submitting || !isActive}
        className="btn disabled:opacity-60"
        title={!isActive ? "this account isn't active on meta — pick a different one" : undefined}
      >
        {submitting ? <Trans>picking…</Trans> : <Trans>pick this</Trans>}
        <span className="btn-arrow">→</span>
      </button>
    </li>
  );
}
